import { authToken, captchaStorageCode, captchaStorageLogin, handleNonMCILogin, useAppDispatch, useAppSelector } from "redux-share";
import Popover from "../popover/popover";
import { shallowEqual } from 'react-redux'
import { conditions, responseValidator } from "helpers";
import { ConditionName, ICaptchaType, ILoginFormDataInterface } from "interfaces";
import { useState, useEffect, useCallback, FormEvent } from "react";
import IMAGES from "../../../../../images/importImages";
import Image from "next/image";
import { API, apiCaller } from "../../../../../services";
import { setAuthToken, setRefreshToken } from "redux-share/slices/userSlice";
import { setLoginPopover } from "redux-share/slices/popoversSlice";
import { toast } from "mobin-toast";
import dynamic from "next/dynamic";

const PhoneInput = dynamic(() => import("../phoneInput/phoneInput"));
const ArrowLeftIcon = dynamic(() => import("../../icon-components/arrowLeft"));
const CodeInput = dynamic(() => import("../codeInput/codeInput"));
const Captcha = dynamic(() => import("../captcha/captcha"));
const CountDown = dynamic(() => import("../countDown/countDown"));

const LoginPopover = () => {

    const dispatch = useAppDispatch();
    useAppSelector(({ popovers: { loginPopover } }) => ({ loginPopover }), shallowEqual);

    
    const [step, setStep] = useState<"PhoneNumber" | "OTP" | "CodeCaptcha" | "LoginCaptcha">("PhoneNumber");
    const [formError, setFormError] = useState<any>({});
    const [codeCaptchaRequired, setCodeCaptchaRequired] = useState<string | null>(null);
    const [loginCaptcha, setLoginCaptcha] = useState<ICaptchaType | undefined>(undefined);
    const [codeCaptcha, setCodeCaptcha] = useState<ICaptchaType | undefined>(undefined);
    const [formData, setFormData] = useState<ILoginFormDataInterface>({ phone: "", phoneFormat: "" });
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [resendCodeTime, setResendCodeTime] = useState<number>(0);

    useEffect(() => {
        if (codeCaptchaRequired) getCaptcha("code");
    }, [codeCaptchaRequired]);

    useEffect(() => {
        if (formData.code && formData.code.length === 6) handleLoginWithCode();
    }, [formData.code])

    useEffect(() => {
        if (step === "OTP") {
            if ('OTPCredential' in window) {
                const ac = new AbortController();
                navigator.credentials.get({
                    // @ts-ignore
                    otp: { transport: ['sms'] },
                    signal: ac.signal
                }).then(otp => {
                    //@ts-ignore
                    setFormData({ ...formData, code: otp!.code });
                }).catch(err => {
                    console.log(err);
                });
            }
        }
    }, [step]);

    const getCaptcha = async (type: string) => {
        const { data, status } = await apiCaller({ url: API().auth.captcha()});
        if (responseValidator(status)) {
            if (type === "login") {
                setLoginCaptcha({ id: data.id, provider: data.provider, image: data.image })
            } else if (type === "code") {
                setCodeCaptcha({ id: data.id, provider: data.provider, image: data.image })
            }
        } else {
            toast({ title: "خطا در دریافت کپچا.", type: "error" })
        }
    }

    const handleLoginCaptchaCode = () => {
        const loginCaptchaValue = loginCaptcha?.value;
        if (!loginCaptchaValue) {
            setFormError({ ...formError, captcha_login_value: "وارد کردن کد کپچا الزامی است" });
        } else if (loginCaptchaValue && loginCaptchaValue.length < 4) {
            setFormError({ ...formError, captcha_login_value: "کد کپچا حداقل 4 کاراکتر می باشد" });
        } else {
            if (loginCaptcha) {
                setFormError({ ...formError, captcha_login_value: "" })
                setLoginCaptcha({ ...loginCaptcha, value: loginCaptchaValue });
            }
        }
    }

    const handleCodeCaptchaCode = (event: FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        const codeCaptchaValue = codeCaptcha?.value;
        if (!codeCaptchaValue) setFormError({ ...formError, captcha_code_value: "وارد کردن کد کپچا الزامی است" });
        else if (codeCaptchaValue && codeCaptchaValue.length < 4) setFormError({ ...formError, captcha_code_value: "کد کپچا حداقل 4 کاراکتر می باشد" });
        else if (codeCaptchaValue) {
            setFormError({ ...formError, captcha_code_value: "" })
            setCodeCaptcha({ ...codeCaptcha, value: codeCaptchaValue });
            generateCode(codeCaptchaValue);
        }
    }

    const handleEnterPhoneNumber = (event: FormEvent<HTMLFormElement>) => {
        setIsLoading(true);
        event.preventDefault();
        if (new Date().getTime() > (Number(captchaStorageCode.get()) * 1000)) generateCode();
        else {
            setFormError({});
            getCaptcha("code");
            setStep("CodeCaptcha");
        }
    }

    const handleLoginWithCode = async (event?: FormEvent<HTMLFormElement>) => {
        if (event) event.preventDefault();
        if (!formData.code) {
            setFormError({ ...formError, code: 'وارد کردن کد یک بار مصرف الزامی می باشد' });
        } else if (formData.code && formData.code.length < 6) {
            setFormError({ ...formError, code: "کد یک بار مصرف را کامل وارد کنید" });
        } else {
            if (captchaStorageCode.get() && new Date().getTime() > (Number(captchaStorageCode.get()) * 1000)) {
                captchaStorageCode.remove();
                setCodeCaptchaRequired(null);
            } else {
                setCodeCaptchaRequired(captchaStorageCode.get());
            }
            const validateCodeData = {
                phone: formData.phone,
                code: formData.code,
            }
            const { data: _data, status } = await apiCaller({ url: API().auth.validateCode(), method: "POST", data: validateCodeData });
            if (responseValidator(status)) {
                const loginData = {
                    phone: formData.phone,
                    captcha_id: loginCaptcha ? loginCaptcha.id : undefined,
                    captcha_value: loginCaptcha ? loginCaptcha.value : undefined,
                    captcha_provider: loginCaptcha ? loginCaptcha.provider : undefined,
                    code: formData.code,
                }
                const { data, status } = await apiCaller({ url: API().auth.login(), method: "POST", data: loginData });
                if (responseValidator(status)) {
                    setCodeCaptchaRequired(null);
                    authToken.set(data);
                    dispatch(setLoginPopover(false));
                    dispatch(setAuthToken(data.access));
                    handleNonMCILogin();
                    if (conditions(ConditionName.isProduction)) dispatch(setRefreshToken(data));
                } else {
                    toast({ title: data.detail, type: "error" });
                    if (data.code && data.code.length > 0) toast({ title: data.code[0], type: "error" });
                    if (loginCaptcha) {
                        if (loginCaptcha.value === "" || data.captcha_required) {
                            captchaStorageLogin.set(data.captcha_required);
                            getCaptcha("login");
                            setStep("LoginCaptcha");
                        }
                    }
                }
            } else {
                toast({ title: "کد وارد شده صحیح نمیباشد.", type: "error" });
            }
        }
    }

    const generateCode = async (captchaValue?: any) => {
        setIsLoading(true);
        const generateCodeData = {
            phone: formData.phone,
            captcha_id: codeCaptcha?.id,
            captcha_value: captchaValue ? captchaValue : codeCaptcha?.value,
            captcha_provider: codeCaptcha?.provider
        }
        const { data, status } = await apiCaller({ url: API().auth.code(), method: "POST", data: generateCodeData});
        if (responseValidator(status)) {
            setCodeCaptchaRequired(null);
            setCodeCaptcha(undefined);
            setResendCodeTime(60);
            setStep("OTP");
            captchaStorageCode.set(data.captcha_required);
            setIsLoading(false);
        } else {
            toast({ title: data.detail, type: "error" });
            if (data.captcha_required) {
                captchaStorageCode.set(data.captcha_required);
                setCodeCaptchaRequired(data.captcha_required);
                setFormError({});
                if (codeCaptchaRequired !== null) getCaptcha("code");
                setStep("CodeCaptcha");
            }
            setIsLoading(false);
        }
    }

    const handleChangeCodeInput = (value: string) => {
        if (value.length === 6) setFormError({ ...formError, code: "" });
        setFormData({ ...formData, code: value });
    }

    const handleBackButton = () => {
        setStep("PhoneNumber");
        setFormData({...formData, code: ""});
    }

    const handleGenerateCode = useCallback(() => {
        setFormData({ ...formData, password: undefined });
        return new Promise((resolve) => {
            if (new Date().getTime() > (Number(captchaStorageCode.get()) * 1000)) {
                generateCode(resolve);
            } else {
                resolve(false);
                setFormError({});
                getCaptcha("code");
            }
        });
    }, [formData.phone, codeCaptcha]);

    let popoverContent = <></>
    let headerContent = <></>

    switch (step) {
        case "PhoneNumber":
            headerContent = (
                <div className={`pl-flex pl-gap-2 ${conditions(ConditionName.isTouchScreenPortrait) ? "pl-p-4" : "pl-px-12 pl-py-[18px]"}`}>
                    <Image src={conditions(ConditionName.isDarkMode) ? IMAGES.loginWhite : IMAGES.loginDark} width={24} height={24} />
                    <p>ورود به سامانه سینما همراه</p>
                </div>
            )
            popoverContent = (
                <div className={`${conditions(ConditionName.isTouchScreenPortrait) ? "pl-p-4" : "pl-px-12 pl-py-[18px]"}`}>
                    <p className="pl-text-[14px] pl-font-medium pl-text-z-gray-700 dark:pl-text-dark-5 pl-mb-16">کاربر گرامی، این محتوا رایگان نیست. برای مشاهده ویدیو لطفا وارد سامانه سینما همراه شوید. برای ورود لطفا شماره موبایل خود را وارد کنید.</p>
                    <form className="pl-flex pl-flex-col pl-gap-4" onSubmit={handleEnterPhoneNumber}>
                        <PhoneInput 
                            error={formError.phone}
                            label="شماره تلفن همراه"
                            onChange={(value) => {
                                setFormData({
                                    ...formData,
                                    phone: value.replaceAll(" ", ""),
                                    phoneFormat: value
                                });
                                setFormError({ ...formError, phone: '' })
                            }}
                            value={formData?.phoneFormat}
                            autoFocus={true}
                            parentClassName="pl-relative"
                            placeholder="09∗∗ ∗∗∗ ∗∗∗∗"
                        />
                        <Captcha
                            captcha={loginCaptcha}
                            handleClickButton={handleLoginCaptchaCode}
                            error={formError.captcha_login_value}
                            getNewCaptcha={() => getCaptcha("login")}
                            setCaptchaValue={setLoginCaptcha}
                        />
                        <button
                            disabled={!formData.phone || formData.phone.length < 11 || isLoading}
                            type="submit" 
                            className="pl-bg-main-light hover:pl-bg-main-700 disabled:pl-bg-main disabled:pl-opacity-50 pl-transition-colors pl-duration-300 pl-rounded-[8px] pl-py-4 pl-mt-10 pl-mb-[90px] pl-text-white pl-text-[15px] pl-font-medium"
                        >
                            ادامه
                        </button>
                    </form>
                </div>
            )
            break;
        case "OTP":
            headerContent = (
                <div className={`pl-flex pl-gap-2 ${conditions(ConditionName.isTouchScreenPortrait) ? "pl-p-4" : "pl-px-12 pl-py-[18px]"}`}>
                    <Image src={conditions(ConditionName.isDarkMode) ? IMAGES.loginWhite : IMAGES.loginDark} width={24} height={24} />
                    <p>ورود کد یکبار مصرف</p>
                    <ArrowLeftIcon className={`pl-fill-z-gray-700 dark:pl-fill-z-secondary-50 pl-mr-auto pl-cursor-pointer`} onClick={handleBackButton}/>
                </div>
            )
            popoverContent = (
                <div className={`${conditions(ConditionName.isTouchScreenPortrait) ? "pl-p-4" : "pl-px-12 pl-py-[18px] pl-mb-[90px]"}`}>
                    <p className="pl-text-z-gray-700 dark:pl-text-dark-5 pl-font-normal">
                        <span>لطفا کد 6 رقمی ارسال شده به شماره</span>
                        <span className="pl-text-main-light pl-font-medium"> {formData.phone} </span>
                        <span>را وارد کنید.</span>
                        <br />
                        <span>ارسال کد ممکن است کمی طول بکشد</span>
                    </p>
                    <form className="pl-flex pl-flex-col" onSubmit={handleLoginWithCode}>
                        <CodeInput onChange={(value) => handleChangeCodeInput(value)} />
                        <button
                            type="submit"
                            className="pl-bg-main-light hover:pl-bg-main-700 disabled:pl-bg-main disabled:pl-opacity-50 pl-transition-colors pl-duration-300 pl-rounded-[8px] pl-py-4 pl-mt-10 pl-text-white pl-text-[15px] pl-font-medium"
                        >
                            تایید کد ارسالی
                        </button>
                    </form>
                    <CountDown 
                        resendCodeTime={resendCodeTime} 
                        setResendCodeTime={setResendCodeTime}
                        handleGenerateCode={handleGenerateCode} 
                    />
                </div>
            )
            break;
        case "CodeCaptcha":
            headerContent = (
                <div className={`pl-flex pl-gap-2 ${conditions(ConditionName.isTouchScreenPortrait) ? "pl-p-4" : "pl-px-12 pl-py-[18px]"}`}>
                    <Image src={conditions(ConditionName.isDarkMode) ? IMAGES.loginWhite : IMAGES.loginDark} width={24} height={24} />
                    <p>ورود کد کپچا</p>
                </div>
            )
            popoverContent = (
                <form className={`pl-flex pl-flex-col ${conditions(ConditionName.isTouchScreenPortrait) ? "pl-p-4" : "pl-px-12 pl-py-[18px]"}`} onSubmit={handleCodeCaptchaCode}>
                    <Captcha
                        captcha={codeCaptcha}
                        error={formError.captcha_code_value}
                        getNewCaptcha={() => getCaptcha("code")}
                        setCaptchaValue={setCodeCaptcha}
                    />
                    <button
                        disabled={!codeCaptcha?.value}
                        type="submit"
                        className="pl-bg-main-light hover:pl-bg-main-700 disabled:pl-bg-main disabled:pl-opacity-50 pl-transition-colors pl-duration-300 pl-rounded-[8px] pl-py-4 pl-mt-10 pl-mb-[90px] pl-text-white pl-text-[15px] pl-font-medium"
                    >
                        تایید کد کپچا
                    </button>
                </form>
            )
            break;
        case "LoginCaptcha":
            headerContent = (
                <div className={`pl-flex pl-gap-2 ${conditions(ConditionName.isTouchScreenPortrait) ? "pl-p-4" : "pl-px-12 pl-py-[18px]"}`}>
                    <Image src={conditions(ConditionName.isDarkMode) ? IMAGES.loginWhite : IMAGES.loginDark} width={24} height={24} />
                    <p>ورود کد کپچا</p>
                </div>
            )
            popoverContent = (
                <form className={`pl-flex pl-flex-col ${conditions(ConditionName.isTouchScreenPortrait) ? "pl-p-4" : "pl-px-12 pl-py-[18px]"}`} onSubmit={handleLoginCaptchaCode}>
                    <Captcha
                        captcha={loginCaptcha}
                        handleClickButton={handleLoginCaptchaCode}
                        error={formError.captcha_login_value}
                        getNewCaptcha={() => getCaptcha("login")}
                        setCaptchaValue={setLoginCaptcha}
                    />
                    <button
                        type="submit"
                        className="pl-bg-main-light hover:pl-bg-main-700 disabled:pl-bg-main disabled:pl-opacity-50 pl-transition-colors pl-duration-300 pl-rounded-[8px] pl-py-4 pl-mt-10 pl-mb-[90px] pl-text-white pl-text-[15px] pl-font-medium"
                    >
                        تایید کد کپچا
                    </button>
                </form>
            )
            break;
        default:
            break;
    }

    return (
        <Popover
            type={"modal"}
            status={conditions(ConditionName.showLoginPopover)}
            onClose={()=> {}}
            isBlurBackground={true}
            isHeader={true}
            customHeader={headerContent}
            classNames="pl-max-w-[541px]"
        >
            {popoverContent}
        </Popover>
    )
}

export default LoginPopover;